import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { ChangeRequest } from "@shared/types/supabase.types";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ChevronDown, GitBranch, Unlink } from "lucide-react";
import { useRef, useState } from "react";
import {
  type LinkedCrSurface,
  useChangeRequest,
  useSetLinkedCr,
} from "../api/change-requests-hooks";
import { changeRequestsQueries } from "../api/change-requests-keys";
import { CrLinkPicker } from "./cr-link-picker";

type LinkedCrControlProps = {
  currentCrId: string | null;
  projectId: string;
  workspaceSlug: string;
  projectSlug: string;
  surface: LinkedCrSurface;
  disabled?: boolean;
};

/**
 * The linked-CR control shared by documents and chats (CR-149 D3/D4/D5).
 *
 * Linked state is a real split control: the primary segment is a link that opens
 * the CR — the one common action — and the caret segment hides relinking and
 * unlinking in a dropdown. The caller is responsible for the feature gate
 * (`show_change_requests`) and, on chats, for only rendering it on rooms.
 */
export function LinkedCrControl({
  currentCrId,
  projectId,
  workspaceSlug,
  projectSlug,
  surface,
  disabled = false,
}: LinkedCrControlProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  // Radix returns focus to the dropdown trigger when the menu closes, which
  // would yank it straight back out of the picker we are about to open.
  const keepFocusForPicker = useRef(false);

  const queryClient = useQueryClient();
  const { data: currentCr, isPending: currentCrPending } = useChangeRequest(
    currentCrId ?? undefined,
  );
  const setLinkedCr = useSetLinkedCr(surface, currentCrId);

  const busy = disabled || setLinkedCr.isPending;

  // Relinking points the surface at a CR whose byId query is usually not cached,
  // so the hold-back below would unmount the whole control until it resolves.
  // The CR was just picked out of the project list, which therefore has it.
  const cachedCr = currentCrId
    ? queryClient
        .getQueryData<ChangeRequest[]>(changeRequestsQueries.byProject(projectId).queryKey)
        ?.find((cr) => cr.id === currentCrId)
    : undefined;
  const displayedCr = currentCr ?? cachedCr;

  // CR-53: an archived CR resolves to nothing through byId while the container
  // keeps its cr_id. Show a non-navigating segment so the stale link stays
  // removable instead of the control silently disappearing.
  const linkedCrArchived = !!currentCrId && !currentCrPending && !currentCr;

  const handleSelect = (crId: string) => {
    setLinkedCr.mutate(crId);
  };

  const handleUnlink = () => {
    setLinkedCr.mutate(null);
  };

  const openPickerFromMenu = () => {
    keepFocusForPicker.current = true;
    setPickerOpen(true);
  };

  // D5: hold the control back until byId settles so a linked surface never
  // flashes the unlinked "Link CR" trigger.
  if (currentCrId && currentCrPending && !cachedCr) {
    return null;
  }

  // D8: the server refuses a link it cannot allow — an archived or cross-project
  // CR, a chat the caller cannot reach — with one constant 42501. Without this
  // the menu item would just close and nothing would happen, which reads as a
  // dead button rather than a refusal.
  const errorAlert = setLinkedCr.isError ? (
    <span role="alert" data-testid="cr-link-error" className="text-destructive text-xs">
      Could not change the linked change request.
    </span>
  ) : null;

  if (!currentCrId) {
    return (
      <div className="flex items-center gap-2">
        {errorAlert}
        <CrLinkPicker
          projectId={projectId}
          workspaceSlug={workspaceSlug}
          projectSlug={projectSlug}
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          onSelect={handleSelect}
          asTrigger
          align="end"
        >
          <Button
            variant="outline"
            size="sm"
            data-testid="cr-link-empty"
            disabled={busy}
            className="text-muted-foreground border-dashed text-xs hover:border-solid"
          >
            <GitBranch className="size-3.5 shrink-0" />
            Link CR
          </Button>
        </CrLinkPicker>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {errorAlert}
      <CrLinkPicker
        projectId={projectId}
        workspaceSlug={workspaceSlug}
        projectSlug={projectSlug}
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onSelect={handleSelect}
        align="end"
      >
        <div className="inline-flex items-center" data-testid="linked-cr-control">
          {displayedCr && !linkedCrArchived ? (
            <Button
              variant="outline"
              size="sm"
              asChild
              className="relative max-w-64 rounded-r-none border-r-0 text-xs focus-visible:z-10"
            >
              <Link
                to="/workspaces/$slug/projects/$projectSlug/cr/$crId"
                params={{ slug: workspaceSlug, projectSlug, crId: displayedCr.id }}
                data-testid="cr-link-primary"
                data-cr-sequential-id={displayedCr.sequential_id}
                title={`CR-${displayedCr.sequential_id} · ${displayedCr.name}`}
              >
                <GitBranch className="size-3.5 shrink-0" />
                <span className="shrink-0">CR-{displayedCr.sequential_id}</span>
                <span className="text-muted-foreground truncate">{displayedCr.name}</span>
              </Link>
            </Button>
          ) : (
            <span
              data-testid="cr-link-archived"
              className="border-input bg-muted text-muted-foreground inline-flex h-8 items-center gap-1.5 rounded-md rounded-r-none border border-r-0 px-2.5 text-xs font-medium shadow-xs"
            >
              <GitBranch className="size-3.5 shrink-0" />
              Archived CR
            </span>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                aria-label="Change request link actions"
                data-testid="cr-link-menu-trigger"
                disabled={busy}
                className="relative rounded-l-none px-2 focus-visible:z-10"
              >
                <ChevronDown className="size-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              onCloseAutoFocus={(event) => {
                if (keepFocusForPicker.current) {
                  keepFocusForPicker.current = false;
                  event.preventDefault();
                }
              }}
            >
              <DropdownMenuItem
                data-testid="cr-link-relink"
                disabled={busy}
                onSelect={openPickerFromMenu}
              >
                <GitBranch className="h-4 w-4 shrink-0" />
                Link to another CR…
              </DropdownMenuItem>
              <DropdownMenuItem
                variant="destructive"
                data-testid="cr-link-unlink"
                disabled={busy}
                onSelect={handleUnlink}
              >
                <Unlink className="h-4 w-4 shrink-0" />
                {linkedCrArchived ? (
                  <span>Unlink from archived CR</span>
                ) : (
                  <span>Unlink from CR-{displayedCr?.sequential_id}</span>
                )}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CrLinkPicker>
    </div>
  );
}
